import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { getListenerJourney } from '@api/listeners/api'
import { IListenerJourney } from '@api/listeners/state.types'
import { ModalPreview } from '@primitive/modal.preview'
import { CardApplicationContainer } from '@smart/cardApplication'
import { CardCommercialOffersContainer } from '@smart/cardKP'
import { CardCompanyContainer } from '@smart/cardCompany'
import { CardContractContainer } from '@smart/cardContract'
import { CardDeliveryContainer } from '@smart/cardDelivery'
import { CardFinalExaminationContainer } from '@smart/cardFinalExamination'
import { CardListenerContainer } from '@smart/cardListener'
import { CardPrintingDiplomasContainer } from '@smart/cardPrintingDiplomas'
import { CardSignatureContainer } from '@smart/cardSignature'

import { ListenerJourney } from './ui'
import { IListenerJourneyContainer, ListenerJourneyNodeAction } from './types'

export const ListenerJourneyContainer = ({isOpen, listenerID, onClose}: IListenerJourneyContainer) => {
    const requestRef = useRef<AbortController | null>(null)
    const [data, setData] = useState<IListenerJourney | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [activeAction, setActiveAction] = useState<ListenerJourneyNodeAction | null>(null)

    const loadJourney = useCallback(async () => {
        if (!listenerID) {
            setData(null)
            setErrorMessage('Не указан номер анкеты слушателя.')
            return
        }

        requestRef.current?.abort()
        const request = new AbortController()
        requestRef.current = request
        setIsLoading(true)
        setErrorMessage('')

        try {
            const response = await getListenerJourney(listenerID, request.signal)
            if (requestRef.current !== request) return
            if (response.status === 499) return

            if (response.status !== 200 || !response.data) {
                setData(null)
                setErrorMessage(response.message || 'Не удалось загрузить полный цикл слушателя.')
                return
            }

            setData(response.data)
        } catch {
            if (requestRef.current !== request) return
            setData(null)
            setErrorMessage('Не удалось загрузить полный цикл слушателя.')
        } finally {
            if (requestRef.current === request) {
                requestRef.current = null
                setIsLoading(false)
            }
        }
    }, [listenerID])

    useEffect(() => {
        if (!isOpen) {
            requestRef.current?.abort()
            requestRef.current = null
            setData(null)
            setErrorMessage('')
            setIsLoading(false)
            setActiveAction(null)
            return
        }

        setData(null)
        loadJourney()
        return () => {
            requestRef.current?.abort()
            requestRef.current = null
        }
    }, [isOpen, listenerID, loadJourney])

    const closeJourney = () => {
        requestRef.current?.abort()
        requestRef.current = null
        setActiveAction(null)
        onClose?.()
    }

    const closeRecord = (reload = true) => {
        setActiveAction(null)
        if (reload) loadJourney()
    }

    const renderActiveRecord = () => {
        if (!activeAction) return null

        switch (activeAction.type) {
            case 'listener':
                return <CardListenerContainer
                    isOpen cardMode='edit' cardID={activeAction.cardID}
                    handlerButtonCancel={() => closeRecord()}
                    handlerButtonOk={() => closeRecord()}
                />
            case 'application':
                return <CardApplicationContainer
                    isOpen cardMode='edit' cardID={activeAction.cardID}
                    handleButtonCancel={() => closeRecord()}
                    handleButtonOk={() => closeRecord()}
                />
            case 'commercialOffer':
                return <CardCommercialOffersContainer
                    isOpen cardMode='edit' cardID={activeAction.cardID}
                    handleButtonCancel={() => closeRecord()}
                    handleButtonOk={() => closeRecord()}
                />
            case 'contract':
                return <CardContractContainer
                    isOpen cardMode='edit' cardID={activeAction.cardID}
                    handlerButtonCancel={() => closeRecord()}
                    handlerButtonOk={() => closeRecord()}
                />
            case 'company':
                return <CardCompanyContainer
                    isOpen cardMode='edit' cardID={activeAction.cardID}
                    handlerButtonCancel={() => closeRecord()}
                    handlerButtonOk={() => closeRecord()}
                />
            case 'signature':
                return <CardSignatureContainer
                    isOpen cardID={activeAction.cardID} contractNumber={activeAction.contractNumber}
                    handleButtonCancel={() => closeRecord()}
                    handleButtonOk={() => closeRecord()}
                />
            case 'finalExamination':
                return <CardFinalExaminationContainer
                    isOpen
                    cardID={activeAction.cardID}
                    fullNameFrom={activeAction.fullNameFrom}
                    attestationType={activeAction.attestationType}
                    dataBase={activeAction.dataBase}
                    handlerButtonCancel={() => closeRecord()}
                    handlerButtonOk={() => closeRecord()}
                />
            case 'printing':
                return <CardPrintingDiplomasContainer
                    isOpen
                    cardID={activeAction.cardID}
                    fullNameFrom={activeAction.fullNameFrom}
                    dataBase={activeAction.dataBase}
                    handlerButtonCancel={() => closeRecord()}
                    handlerButtonOk={() => closeRecord()}
                />
            case 'scan':
                return <ModalPreview
                    isOpen
                    fileUrl={activeAction.fileUrl}
                    fileName={activeAction.fileName}
                    handleButtonCancel={() => closeRecord(false)}
                />
            case 'delivery':
                return <CardDeliveryContainer
                    isOpen cardID={activeAction.cardID}
                    handlerButtonCancel={() => closeRecord()}
                    handlerButtonOk={() => closeRecord()}
                />
        }
    }

    if (!isOpen) return null

    return createPortal(<>
        <ListenerJourney
            isOpen
            listenerID={listenerID}
            data={data}
            isLoading={isLoading}
            errorMessage={errorMessage}
            onClose={closeJourney}
            onRetry={loadJourney}
            onNodeAction={setActiveAction}
        />
        {renderActiveRecord()}
    </>, document.body)
}